'use client';

import { useCallback, useMemo, useState } from 'react';

import { Button } from '@wci-tech/core-ui';
import type { ButtonProps } from '@wci-tech/core-ui';

import { ANALYTICS_EVENTS, trackEvent } from '@/analytics';
import {
  lockCalendlyPage,
  unlockCalendlyPage,
} from '@/services/integrations/calendly/Calendly.scroll-lock';
import { openCalendlyPopup } from '@/services/integrations/calendly/Calendly.service';

import type { BookADemoButtonProps } from './BookADemoButton.types';

/**
 * Renders a reusable button that opens a Calendly popup for demo booking.
 *
 * This component wraps the shared `Button` component and adds Calendly-specific
 * behavior. When clicked, it lazily loads the Calendly assets, opens the popup,
 * and exposes lifecycle callbacks for success and error handling.
 *
 * The rendered button:
 * - uses `type="button"`
 * - shows loading state while the popup is being opened
 * - becomes disabled while the open action is in progress
 * - uses `children`, `buttonText`, or `"Book a Demo"` as the label, in that order
 *
 * @param props - Props used to configure the button and Calendly behavior.
 * @param props.calendlyUrl - Full Calendly event URL to open in the popup.
 * @param props.buttonText - Optional fallback text shown inside the button when `children` is not provided.
 * @param props.children - Optional button content. Takes precedence over `buttonText`.
 * @param props.onOpen - Optional callback fired after the Calendly popup is successfully triggered.
 * @param props.onError - Optional callback fired when the Calendly popup cannot be opened or the URL is missing.
 * @param props.disabled - Disables the button. The button is also disabled while Calendly is opening.
 * @param props.loading - Optional external loading state. If not provided, internal opening state is used.
 * @param props.restButtonProps - Any additional props supported by the shared `Button` component, such as `shape`, `variant`, `size`, `customStyles`, `className`, and similar button configuration props.
 *
 * @returns A reusable button component that opens the Calendly popup on click.
 *
 * @usage
 * Basic usage
 * ```tsx
 * <BookADemoButton
 *   buttonText="Book a Demo"
 *   calendlyUrl="https://calendly.com/username/demo"
 * />
 * ```
 *
 * @usage
 * Usage with shared button styling and shape
 * ```tsx
 * <BookADemoButton
 *   customStyles={{ base: styles.button }}
 *   shape="pill"
 *   buttonText={t('bookDemo')}
 *   calendlyUrl={appConfig.integrations.calendly.eventUrl}
 * />
 * ```
 *
 * @example
 * Usage with custom button content
 * ```tsx
 * <BookADemoButton calendlyUrl={appConfig.integrations.calendly.eventUrl}>
 *   Schedule a Call
 * </BookADemoButton>
 * ```
 *
 * @usage
 * Usage with lifecycle callbacks
 * ```tsx
 * <BookADemoButton
 *   calendlyUrl={appConfig.integrations.calendly.eventUrl}
 *   buttonText="Talk to Sales"
 *   onOpen={() => {
 *     console.log('Calendly popup opened');
 *   }}
 *   onError={(error) => {
 *     console.error('Failed to open Calendly', error);
 *   }}
 * />
 * ```
 */

const BookADemoButton = ({
  calendlyUrl,
  buttonText,
  children,
  onOpen,
  onError,
  disabled,
  loading,
  ...restButtonProps
}: BookADemoButtonProps) => {
  const [isOpening, setIsOpening] = useState(false);

  const handleClick = useCallback(async () => {
    const eventData = {
      page: window.location.pathname,
      calendly_url: calendlyUrl,
    };

    trackEvent(ANALYTICS_EVENTS.BOOK_A_DEMO_CLICKED, eventData);

    if (!calendlyUrl?.trim()) {
      const error = new Error('Calendly URL is missing');

      console.error(error.message);

      onError?.(error);

      return;
    }

    try {
      setIsOpening(true);

      await openCalendlyPopup({
        url: calendlyUrl,
        beforeOpen: lockCalendlyPage,
      });

      onOpen?.();
    } catch (error) {
      /**
       * If initialization fails after locking, always restore
       * the document.
       */
      unlockCalendlyPage();

      console.error('Failed to open Calendly popup', error);

      onError?.(error);
    } finally {
      setIsOpening(false);
    }
  }, [calendlyUrl, onError, onOpen]);

  const finalButtonProps = useMemo(
    () =>
      ({
        ...restButtonProps,
        type: 'button',
        onClick: handleClick,
        disabled: Boolean(disabled || isOpening),
        loading: loading ?? isOpening,
        children: children ?? buttonText ?? 'Book a Demo',
      }) as ButtonProps,
    [restButtonProps, handleClick, disabled, isOpening, loading, children, buttonText],
  );

  return <Button {...finalButtonProps} />;
};

export default BookADemoButton;
